import { useEffect, useMemo } from 'react';

import { Close } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Formik, useField } from 'formik';
import {
    TransactionPricingCardType,
    TransactionPricingOfferPayload,
    TransactionPricingType,
} from 'types/graphql';

import MainCard from 'src/components/MainCard/MainCard';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useTransactionChargeContext } from 'src/contexts/TransactionChargeProvider';
import { useAdditionalCharges, useTransactionPricing } from 'src/hooks';
import { useTransactionData } from 'src/hooks/useTransactionCharges';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

import { AdditionalCharges } from './AdditionalCharges';
import AdditionalTariffs from './AdditionalTariffs';
import { CardCharge } from './CardCharge';
import { Locality } from './Locality';
import { PCIComplience } from './PCIComplience';

interface TransactionChargesProps {
    editTransactionCharges: boolean;
    handleOpenTransactionCharges?: () => void;
    handleEditTransactionCharges?: () => void;
    name: string;
    serviceType: string;
    hideEditPen?: boolean;
    onClose?: () => void;
    onSave?: (updatedValues: object) => void;
}

export type ChargeValue = {
    purchasesPercentage?: string;
    purchasescentsPerItem?: string;
    refundsPercentage?: string;
    refundsCentsPerItem?: string;
    amount?: string;
};

export type ChargesValue = {
    [key: string]: ChargeValue;
};

export type TransactionChargesFormValues = {
    [key: string]: ChargesValue;
};

export type CardOption = {
    label: string;
    isConstant: boolean;
    id?: number;
    name?: string;
    type?: TransactionPricingCardType;
    cardCategory?: TransactionPricingType;
    baseRate?: number;
    percentageBaseRate?: number;
    percentageLowestRate?: number;
    percentageHighestRate?: number;
    perItemBaseRate?: number;
    perItemLowestRate?: number;
    perItemHighestRate?: number;
    baseRatePercentage?: number;
};

export const TransactionCharges: React.FC<TransactionChargesProps> = ({
    editTransactionCharges,
    handleOpenTransactionCharges,
    handleEditTransactionCharges,
    name,
    serviceType,
    hideEditPen,
    onClose,
    onSave,
}) => {
    const { activeTransactionPricingOffers } = useTransactionPricing();
    const { selectedTransactionOfferId, selectedActiveTransactionPricingOffer } =
        useTransactionChargeContext();
    const [{ value: initialValues }] = useField(name);
    const [__, ___, { setValue: setSeletedTransactionOffer }] = useField('transactionCharges');
    const { additionalCharges } = useAdditionalCharges(serviceType);

    const { LOCALITY_CONSTANT, ONLINE_LOCALITY_CONSTANT } = useTransactionData();
    const { merchantApplication } = useAddMerchantContext();

    const selectedTransactionOffer: TransactionPricingOfferPayload = useMemo(() => {
        if (selectedActiveTransactionPricingOffer) {
            return selectedActiveTransactionPricingOffer;
        } else if (selectedTransactionOfferId) {
            return activeTransactionPricingOffers.find((el) => {
                return el.id === Number(selectedTransactionOfferId);
            });
        }
        return null;
    }, [
        activeTransactionPricingOffers,
        selectedActiveTransactionPricingOffer,
        selectedTransactionOfferId,
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleClose = () => {
        if (editTransactionCharges) {
            handleEditTransactionCharges();
        } else {
            handleOpenTransactionCharges();
        }
    };
    initialValues.additionalCharges = additionalCharges;
    if (serviceType === 'ONLINE') {
        initialValues.locality = ONLINE_LOCALITY_CONSTANT;
    } else {
        initialValues.locality = LOCALITY_CONSTANT;
    }

    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <Box style={{ height: 'calc(100vh - 80px)', overflow: 'auto' }}>
            <Formik
                validateOnBlur={false}
                validateOnChange={false}
                initialValues={initialValues}
                onSubmit={(values) => {
                    setSeletedTransactionOffer({
                        id: selectedTransactionOfferId,
                        additionalTariffs: values?.additionalTariffs,
                        pciComplianceCharges: {
                            additionChargers: values?.pciComplianceCharges?.additionChargers,
                            worldPayCharges: values?.pciComplianceCharges?.worldPayCharges,
                        },
                        additionalCharges: values?.additionalCharges,
                        commercialCards: values?.commercialCards,
                        otherCards: values?.otherCards,
                        personalCards: values?.personalCards,
                        locality: values?.locality,
                    });
                    onSave && onSave(values);
                }}
            >
                {({ handleSubmit, errors, isValid }) => {
                    return (
                        <Box display="flex" flexDirection="column">
                            <MainCard>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Typography fontSize="24px" color="#575757" marginBottom="16px">
                                        Transaction Charges
                                    </Typography>
                                    {!editTransactionCharges && (
                                        <IconButton
                                            onClick={
                                                hideEditPen ? onClose : handleEditTransactionCharges
                                            }
                                            disabled={isEditingDisabled}
                                        >
                                            {hideEditPen ? <Close /> : <EditIcon />}
                                        </IconButton>
                                    )}
                                </Stack>

                                <Stack direction="column" gap={2}>
                                    <CardCharge
                                        name="personalCards"
                                        heading="Personal Cards"
                                        editable={editTransactionCharges}
                                    />
                                    <CardCharge
                                        name="commercialCards"
                                        heading="Commercial Cards"
                                        editable={editTransactionCharges}
                                    />
                                    <CardCharge
                                        name="otherCards"
                                        heading="Other Cards"
                                        editable={editTransactionCharges}
                                    />

                                    <AdditionalCharges
                                        name="additionalCharges"
                                        serviceType={serviceType}
                                        selectedTransactionOffer={selectedTransactionOffer}
                                    />

                                    <Locality
                                        name="locality"
                                        serviceType={serviceType}
                                        selectedTransactionOffer={selectedTransactionOffer}
                                    />

                                    <AdditionalTariffs
                                        name="additionalTariffs"
                                        editable={editTransactionCharges}
                                    />

                                    <PCIComplience
                                        name="pciComplianceCharges"
                                        editTransactionCharges={editTransactionCharges}
                                    />
                                </Stack>
                            </MainCard>

                            <Stack direction="row" justifyContent="flex-end" gap={3} mt={2}>
                                <Button
                                    variant="main"
                                    disabled={!isValid}
                                    onClick={() => {
                                        return handleSubmit();
                                    }}
                                >
                                    Save
                                </Button>

                                <Button variant="main" onClick={handleClose}>
                                    Close
                                </Button>
                            </Stack>
                        </Box>
                    );
                }}
            </Formik>
        </Box>
    );
};
